import React from "react";

import { metadata, statusMeta } from "../../campaignUtils";
import { Action, Alert, Card, EmptyState } from "../../shared/StudioControls";

const totalFor = (counts, statuses) => (counts || [])
  .filter(row => statuses.includes(row.status))
  .reduce((sum, row) => sum + Number(row.total || 0), 0);

const StatusChart = ({ counts }) => {
  const rows = (counts || []).map(row => ({ ...row, total: Number(row.total || 0) }))
    .sort((a, b) => b.total - a.total);
  const max = Math.max(1, ...rows.map(row => row.total));
  if (!rows.length) return <p className="cs-muted">No delivery results yet.</p>;
  return (
    <ul className="cs-detail-bars" aria-label="Delivery results by status">
      {rows.map(row => {
        const meta = statusMeta(row.status);
        return (
          <li key={row.status}>
            <div className="cs-detail-bars__label">
              <span>{meta.label}</span>
              <strong>{row.total.toLocaleString()}</strong>
            </div>
            <div className="cs-detail-bars__track" role="img" aria-label={`${meta.label}: ${row.total}`}>
              <span className={`is-${meta.tone}`} style={{ width: `${(row.total / max) * 100}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const quantity = value => Number(value || 0).toLocaleString("en-LK");
const orderValue = value => `LKR ${Number(value || 0).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const share = (customers, sent) => sent ? `${((Number(customers || 0) / sent) * 100).toFixed(1)}% of messaged customers` : "0% of messaged customers";
const measurementLabels = {
  delivered: "Delivered", opened: "Opened", clicked: "Clicked", attributed_revenue: "Attributed revenue",
  discount_cost: "Discount cost", communication_cost: "Communication cost", net_contribution: "Net contribution",
  unsubscribe_rate: "Unsubscribe rate", complaint_rate: "Complaint rate", control_group_uplift: "Control-group uplift"
};

const ActivityMetric = ({ label, total, customers, detail, display }) => (
  <div className="cs-outcome-metric">
    <span>{label}</span>
    <strong>{display || quantity(total)}</strong>
    <small>{detail || `${quantity(customers)} customers`}</small>
  </div>
);

const ItemList = ({ title, rows, countLabel }) => (
  <Card title={title}>
    {!rows.length ? <p className="cs-muted">No items recorded in this window.</p> : (
      <div className="cs-table-wrap" tabIndex={0} role="region" aria-label={title}>
        <table className="cs-table">
          <thead><tr><th scope="col">Item</th><th scope="col">{countLabel}</th><th scope="col">Quantity</th></tr></thead>
          <tbody>{rows.map((row, index) => <tr key={`${row.name}-${index}`}>
            <th scope="row">{row.name}</th><td>{quantity(row.orders || row.bookings)}</td><td>{quantity(row.quantity)}</td>
          </tr>)}</tbody>
        </table>
      </div>
    )}
  </Card>
);

export const AnalyticsStep = ({ campaign, counts, logs, outcomes, windowDays, loading, error, onWindowChange, onRetry }) => {
  const audience = Number(campaign.payload.audience_count || 0);
  const submitted = totalFor(counts, ["submitted"]);
  const attention = totalFor(counts, ["failed", "unknown", "skipped"]);
  const pending = totalFor(counts, ["queued", "processing"]);
  const recent = logs && Array.isArray(logs.data) ? logs.data.filter(log => !metadata(log).test).slice(0, 8) : [];

  return (
    <div className="cs-analytics-panel">
      <Card title="Customer activity after a notification" description="Activity by customers with a submitted personalized notification. These are time-based observations, not proof the notification caused the action.">
        <div className="cs-outcome-toolbar">
          <span>Activity after first send</span>
          <div className="cs-outcome-windows" role="group" aria-label="Activity window">
            {[7, 14, 30].map(days => <button type="button" key={days} aria-pressed={windowDays === days}
              className={windowDays === days ? "is-active" : ""} onClick={() => onWindowChange(days)}>{days} days</button>)}
          </div>
          {outcomes && outcomes.calculated_at && <small className="cs-muted">Updated {new Date(outcomes.calculated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</small>}
        </div>
        {error && <Alert tone="danger" title="Activity could not be loaded">{error} <Action onClick={onRetry}>Try again</Action></Alert>}
        {loading && <p role="status" className="cs-muted">Loading customer activity…</p>}
        {!loading && outcomes && (outcomes.sample_preview ? (
          <EmptyState title="Sample preview has no customer activity" description="This sample was not sent to customers. Customer activity appears here after live notifications are submitted." />
        ) : outcomes.sent_customers === 0 ? (
          <EmptyState title="No customer notifications submitted yet" description="Orders, bookings and feature use will appear after a live notification is submitted." />
        ) : (
          <React.Fragment>
            <p className="cs-muted">A message was submitted to the provider for {quantity(outcomes.sent_customers)} customers. Cancelled orders and bookings are excluded.</p>
            <div className="cs-outcome-metrics">
              <ActivityMetric label="Scenario action rate" display={!outcomes.business || !outcomes.business.conversion || outcomes.business.conversion.rate_percent == null ? "Unavailable" : `${outcomes.business.conversion.rate_percent}%`}
                detail={!outcomes.business || !outcomes.business.conversion || outcomes.business.conversion.rate_percent == null ? "No matching scenario definition" : `${quantity(outcomes.business.conversion.customers)} of ${quantity(outcomes.sent_customers)} submitted customers${outcomes.business.conversion.provisional ? " · provisional window" : ""}`} />
              <ActivityMetric label="Completed order value" display={orderValue(outcomes.business && outcomes.business.completed_order_value)} detail="Product and lab orders completed after send; not attributed revenue" />
            </div>
            <div className="cs-outcome-metrics">
              <ActivityMetric label="Product orders" total={outcomes.medication_orders.total} customers={outcomes.medication_orders.customers} detail={`${share(outcomes.medication_orders.customers, outcomes.sent_customers)} · ${orderValue(outcomes.medication_orders.order_value)} order value`} />
              <ActivityMetric label="Lab bookings" total={outcomes.lab_bookings.total} customers={outcomes.lab_bookings.customers} detail={`${share(outcomes.lab_bookings.customers, outcomes.sent_customers)} · ${orderValue(outcomes.lab_bookings.order_value)} booking value`} />
              <ActivityMetric label="Consultations booked" total={outcomes.consultations.total} customers={outcomes.consultations.customers} detail={share(outcomes.consultations.customers, outcomes.sent_customers)} />
              <ActivityMetric label="Biomarker use" total={outcomes.features.biomarkers.events} customers={outcomes.features.biomarkers.customers} detail={`${quantity(outcomes.features.biomarkers.customers)} customers · feature events`} />
              <ActivityMetric label="Period Tracker use" total={outcomes.features.period_tracker.events} customers={outcomes.features.period_tracker.customers} detail={`${quantity(outcomes.features.period_tracker.customers)} customers · feature events`} />
              <ActivityMetric label="Move & Earn" total={outcomes.move_and_earn.events} customers={outcomes.move_and_earn.customers} detail={`${quantity(outcomes.move_and_earn.customers)} customers · ${quantity(outcomes.move_and_earn.points)} points earned`} />
            </div>
            <div className="cs-analytics-grid cs-outcome-items">
              <ItemList title="Products ordered" rows={outcomes.medications || []} countLabel="Orders" />
              <ItemList title="Lab tests booked" rows={outcomes.lab_tests || []} countLabel="Bookings" />
            </div>
          </React.Fragment>
        ))}
      </Card>

      <Card title="Measurement coverage" description="These business metrics need additional event or cost data. Unavailable does not mean zero.">
        <div className="cs-table-wrap" tabIndex={0} role="region" aria-label="Unavailable business metrics">
          <table className="cs-table">
            <thead><tr><th scope="col">Metric</th><th scope="col">Status</th><th scope="col">Why</th></tr></thead>
            <tbody>{Object.entries((outcomes && outcomes.unavailable) || {}).map(([key, reason]) => <tr key={key}>
              <th scope="row">{measurementLabels[key] || key}</th><td>Unavailable</td><td>{reason}</td>
            </tr>)}</tbody>
          </table>
        </div>
        {!outcomes && <p className="cs-muted">Load notification activity to see measurement coverage.</p>}
      </Card>

      <div className="cs-detail-metrics">
        <div><span>Audience</span><strong>{audience.toLocaleString()}</strong></div>
        <div><span>Submitted</span><strong>{submitted.toLocaleString()}</strong></div>
        <div><span>In progress</span><strong>{pending.toLocaleString()}</strong></div>
        <div className={attention ? "has-attention" : ""}><span>Needs attention</span><strong>{attention.toLocaleString()}</strong></div>
      </div>

      <div className="cs-analytics-grid">
        <Card title="Delivery status" description="Current provider submission results for this notification set.">
          <StatusChart counts={counts} />
        </Card>
        <Card title="Recent delivery activity" description="Latest customer-send records; internal tests are excluded.">
          {!recent.length ? <EmptyState title="No customer sends yet" description="Results will appear after the automated schedule starts delivery." /> : (
            <div className="cs-table-wrap">
              <table className="cs-table">
                <thead><tr><th>Status</th><th>Channel</th><th>Time</th></tr></thead>
                <tbody>{recent.map(log => {
                  const meta = statusMeta(log.status);
                  const details = metadata(log);
                  return (
                    <tr key={log.id}>
                      <td><span className={`cs-pill cs-pill--${meta.tone}`}>{meta.label}</span></td>
                      <td>{log.channel || details.channel || "—"}</td>
                      <td>{log.created_at ? new Date(log.created_at).toLocaleString("en-GB") : "—"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
